/* eslint-disable */
import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import axios from 'axios'
import { Download, RefreshCw } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Input } from '../../Components/ui/input'
import { Button } from '../../Components/ui/button'
import { Label } from '../../Components/ui/label'
import { CalendarDatePicker } from '../../Components/ui/calendar-date-picker'
import { get, getConfig } from '../../http/restservice'
import env from '../../env/env'

const NAV_SCHEMES_ENDPOINT = `${env.API_URL}mutualfundservice/api/basketData/currentUniqueSchemeCodes`
const NAV_DOWNLOAD_ENDPOINT = `${env.API_URL}mutualfundservice/api/basketData/downloadNavHist`

const NavDownload = () => {
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date()
    const monthAgo = new Date()
    monthAgo.setDate(today.getDate() - 30)
    return { from: monthAgo, to: today }
  })
  const [schemes, setSchemes] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSchemeCode, setSelectedSchemeCode] = useState('')
  const [isLoadingSchemes, setIsLoadingSchemes] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState('')

  const selectedScheme = useMemo(
    () => schemes.find((scheme) => scheme.accordSchemeCode === selectedSchemeCode) || null,
    [schemes, selectedSchemeCode],
  )

  const filteredSchemes = useMemo(() => {
    if (!searchQuery.trim()) {
      return schemes
    }
    const query = searchQuery.trim().toLowerCase()
    return schemes.filter((scheme) => {
      return (
        scheme?.schemeName?.toLowerCase()?.includes(query) ||
        scheme?.bseSchemeCode?.toLowerCase()?.includes(query) ||
        String(scheme?.accordSchemeCode || '').toLowerCase().includes(query)
      )
    })
  }, [schemes, searchQuery])

  const fetchSchemes = async () => {
    setIsLoadingSchemes(true)
    setError('')
    try {
      const response = await get(NAV_SCHEMES_ENDPOINT)
      if (response?.isError) {
        throw new Error(response?.response?.message || 'Failed to fetch schemes')
      }
      const schemesList = Array.isArray(response) ? response : response?.data || []
      setSchemes(schemesList)
    } catch (e) {
      setError(e?.message || 'Unable to load schemes')
    } finally {
      setIsLoadingSchemes(false)
    }
  }

  useEffect(() => {
    fetchSchemes()
  }, [])

  const handleDownload = async () => {
    if (!selectedScheme) {
      setError('Please select a fund first')
      return
    }
    if (!dateRange?.from || !dateRange?.to) {
      setError('Please select both From and To dates')
      return
    }

    setError('')
    setIsDownloading(true)
    try {
      const payload = {
        schemeName: selectedScheme.schemeName,
        bseSchemeCode: selectedScheme.bseSchemeCode,
        accordSchemeCode: selectedScheme.accordSchemeCode,
        fromDate: format(dateRange.from, 'yyyy-MM-dd'),
        toDate: format(dateRange.to, 'yyyy-MM-dd'),
      }

      const config = await getConfig({ 'Content-Type': 'application/json' })
      const response = await axios.post(NAV_DOWNLOAD_ENDPOINT, payload, {
        ...config,
        responseType: 'blob',
      })

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.ms-excel',
      })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      const fileName = `nav-history-${selectedScheme.bseSchemeCode || selectedScheme.accordSchemeCode}-${format(
        new Date(),
        'yyyy-MM-dd',
      )}.xlsx`

      link.href = downloadUrl
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to download NAV file')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className='mx-auto p-2 md:p-6 space-y-4 md:space-y-6'>
      <div>
        <h1 className='text-xl md:text-2xl lg:text-3xl font-bold tracking-tight'>NAV Download</h1>
        <p className='text-muted-foreground text-xs md:text-sm'>
          Select a fund and date range to download NAV history Excel file.
        </p>
      </div>

      <Card>
        <CardHeader className='px-3 md:px-6'>
          <CardTitle className='text-base md:text-lg'>Select Fund and Date Range</CardTitle>
          <CardDescription className='text-xs md:text-sm'>
            Search by scheme name, BSE scheme code, or accord scheme code.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid gap-2 max-w-xl'>
            <Label htmlFor='fund-search'>Search Fund</Label>
            <Input
              id='fund-search'
              placeholder='Type fund name or scheme code...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className='grid gap-2 max-w-xl'>
            <Label htmlFor='fund-select'>Select Fund</Label>
            <select
              id='fund-select'
              className='h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              value={selectedSchemeCode}
              onChange={(e) => setSelectedSchemeCode(e.target.value)}
              disabled={isLoadingSchemes}
            >
              <option value=''>
                {isLoadingSchemes ? 'Loading funds...' : 'Choose a fund'}
              </option>
              {filteredSchemes.map((scheme) => (
                <option key={scheme.accordSchemeCode} value={scheme.accordSchemeCode}>
                  {scheme.schemeName} ({scheme.bseSchemeCode})
                </option>
              ))}
            </select>
            {!isLoadingSchemes && filteredSchemes.length === 0 && (
              <p className='text-xs text-muted-foreground'>No funds found for current search.</p>
            )}
          </div>

          <div className='grid gap-2'>
            <Label>Select Date Range</Label>
            <CalendarDatePicker date={dateRange} onDateSelect={setDateRange} />
          </div>

          {selectedScheme && (
            <div className='p-3 rounded-md border bg-secondary-50 text-xs md:text-sm'>
              <p>
                <span className='font-semibold'>Scheme Name:</span> {selectedScheme.schemeName}
              </p>
              <p>
                <span className='font-semibold'>BSE Scheme Code:</span> {selectedScheme.bseSchemeCode}
              </p>
              <p>
                <span className='font-semibold'>Accord Scheme Code:</span>{' '}
                {selectedScheme.accordSchemeCode}
              </p>
            </div>
          )}

          {error && <p className='text-sm text-error-500'>{error}</p>}

          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              onClick={fetchSchemes}
              disabled={isLoadingSchemes}
              className='gap-2'
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingSchemes ? 'animate-spin' : ''}`} />
              Refresh Funds
            </Button>
            <Button onClick={handleDownload} disabled={isDownloading || !selectedScheme} className='gap-2'>
              {isDownloading ? (
                <RefreshCw className='h-4 w-4 animate-spin' />
              ) : (
                <Download className='h-4 w-4' />
              )}
              {isDownloading ? 'Downloading...' : 'Download NAV File'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default NavDownload
